import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { t } from '../i18n/idioma';
import { useSession } from '../state/SessionContext';
import { EMAIL_MAX, SENHA_MAX, SENHA_MIN, validarEmail, validarSenha } from '../screens/entrada/validar';

/** As classes de cada lugar: a tela de entrada e o cartão da landing aberta (as mesmas da confirmação de e-mail). */
const CLASSES = {
  tela: {
    form: 'pilha', campo: 'campo', rotulo: 'campo__rotulo', caixa: 'campo__caixa', botao: 'btn btn--primary',
    elo: 'elo', erro: 'aviso', nota: 'aviso aviso--status', miudo: 'footnote', regras: 'footnote'
  },
  landing: {
    form: 'lp-gump__corpo', campo: 'lp-campo', rotulo: '', caixa: '', botao: 'lp-btn lp-btn--ouro',
    elo: 'lp-elo', erro: 'lp-aviso', nota: 'lp-aviso lp-aviso--nota', miudo: 'lp-miudo', regras: 'lp-regras'
  }
} as const;

const ESPERA_S = 60;

/**
 * "Esqueci a senha" (docs/techspec/techspec-esqueci-senha.md). Dois passos:
 * 1. o e-mail da conta → o servidor manda o código (a resposta é sempre a mesma: não diz se o e-mail tem conta);
 * 2. o código de 6 números + a senha nova → a senha troca, as outras sessões caem e o jogador entra.
 * Pelo botão do e-mail, já abre no passo 2 com o e-mail e o código preenchidos; falta só a senha nova.
 */
export function RedefinirSenha({ variante, emailInicial = '', codigoInicial = '', serial, onVoltar }: {
  variante: keyof typeof CLASSES;
  emailInicial?: string;
  codigoInicial?: string;
  serial?: string;
  onVoltar(): void;
}) {
  const { redefinirSenha, busy, error, clearError } = useSession();
  const c = CLASSES[variante];
  const doLink = !!emailInicial && codigoInicial.length === 6;
  const [passo, setPasso] = useState<'pedir' | 'codigo'>(doLink ? 'codigo' : 'pedir');
  const [email, setEmail] = useState(emailInicial.includes('*') ? '' : emailInicial);
  const [codigo, setCodigo] = useState(codigoInicial.replace(/\D/g, '').slice(0, 6));
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [pedindo, setPedindo] = useState(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const [nota, setNota] = useState<string | null>(null);
  const [espera, setEspera] = useState(0);
  const primeiro = useRef<HTMLInputElement>(null);
  const erro = erroLocal ?? error;

  useEffect(() => {
    if (espera <= 0) return;
    const id = window.setTimeout(() => setEspera(s => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [espera]);

  // O cursor vai para o primeiro campo vazio do passo.
  useEffect(() => {
    const id = passo === 'pedir' ? 'senha-esqueci-email' : codigo.length === 6 ? 'senha-nova' : 'senha-codigo';
    document.getElementById(id)?.focus({ preventScroll: true });
  }, [passo]); // eslint-disable-line react-hooks/exhaustive-deps

  const limpar = () => { setErroLocal(null); setNota(null); clearError(); };

  const pedir = async () => {
    const erroEmail = validarEmail(email);
    if (erroEmail) { setErroLocal(erroEmail); document.getElementById('senha-esqueci-email')?.focus(); return false; }
    setPedindo(true);
    try {
      await api.esqueciSenha(email.trim());
      setEspera(ESPERA_S);
      setNota(t('senha.enviado', { email: email.trim() }));
      return true;
    } catch (err) {
      setErroLocal(err instanceof ApiError ? err.message : t('comum.algoDeuErrado'));
      return false;
    } finally {
      setPedindo(false);
    }
  };

  const enviarEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (pedindo) return;
    limpar();
    if (await pedir()) setPasso('codigo');
  };

  const reenviar = () => {
    if (espera > 0 || pedindo || busy) return;
    limpar();
    void pedir();
  };

  const salvar = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    limpar();
    if (codigo.length !== 6) { setErroLocal(t('auth.codigoInvalido')); document.getElementById('senha-codigo')?.focus(); return; }
    const erroSenha = validarSenha(senha, email, true);
    if (erroSenha) { setErroLocal(erroSenha); document.getElementById('senha-nova')?.focus(); return; }
    void redefinirSenha(email, codigo, senha, serial);
  };

  const campo = (id: string, rotulo: string, input: ReactNode, extra?: ReactNode) => (
    <div className={c.campo}>
      {c.rotulo
        ? <span className={c.rotulo}><label htmlFor={id}>{rotulo}</label>{extra}</span>
        : <div className="lp-campo__linha"><label htmlFor={id}>{rotulo}</label>{extra}</div>}
      {c.caixa ? <span className={c.caixa}>{input}</span> : input}
    </div>
  );

  const avisos = (
    <>
      {erro && <p className={c.erro} id="senha-erro" role="alert">{erro}</p>}
      {nota && !erro && <p className={c.nota} role="status">{nota}</p>}
    </>
  );

  if (passo === 'pedir') {
    return (
      <form className={c.form} onSubmit={enviarEmail} noValidate>
        <p className={c.miudo}>{t('senha.pedirTexto')}</p>
        {campo('senha-esqueci-email', t('auth.email'), (
          <input
            ref={primeiro}
            id="senha-esqueci-email"
            name="email"
            type="email"
            value={email}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={EMAIL_MAX}
            disabled={pedindo}
            aria-describedby={erro ? 'senha-erro' : undefined}
            onChange={e => { setEmail(e.target.value); limpar(); }}
          />
        ))}
        {avisos}
        <button type="submit" className={c.botao} disabled={pedindo}>
          {pedindo ? t('senha.enviando') : t('senha.enviar')}
        </button>
        <p className={c.miudo}>
          <button type="button" className={c.elo} onClick={() => { limpar(); onVoltar(); }}>{t('senha.voltarEntrar')}</button>
        </p>
      </form>
    );
  }

  return (
    <form className={c.form} onSubmit={salvar} noValidate>
      <p className={c.miudo} role="status">{t('senha.codigoTexto', { email: email.trim() })}</p>
      {/* Para o gerenciador de senhas saber de qual conta é a senha nova. */}
      <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
      {campo('senha-codigo', t('confirmar.codigo'), (
        <input
          id="senha-codigo"
          name="codigo"
          value={codigo}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="000000"
          spellCheck={false}
          disabled={busy}
          onChange={e => { setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6)); limpar(); }}
          style={{ letterSpacing: '.35em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
        />
      ))}
      {campo('senha-nova', t('senha.nova'), (
        <div className={variante === 'landing' ? 'lp-senha' : 'campo__senha'}>
          <input
            id="senha-nova"
            name="new-password"
            type={mostrar ? 'text' : 'password'}
            value={senha}
            placeholder={t('entrada.dicaSenha')}
            autoComplete="new-password"
            minLength={SENHA_MIN}
            maxLength={SENHA_MAX}
            disabled={busy}
            aria-describedby={erro ? 'senha-erro' : undefined}
            onChange={e => { setSenha(e.target.value); limpar(); }}
          />
        </div>
      ), (
        <button type="button" className={c.elo} aria-pressed={mostrar} onClick={() => setMostrar(v => !v)}>
          {mostrar ? t('entrada.ocultarSenha') : t('entrada.mostrarSenha')}
        </button>
      ))}
      {avisos}
      <button type="submit" className={c.botao} disabled={busy || codigo.length !== 6 || !senha}>
        {busy ? t('senha.salvando') : t('senha.salvar')}
      </button>
      <p className={c.miudo}>{t('senha.nota')}</p>
      <p className={c.miudo}>
        <button type="button" className={c.elo} onClick={reenviar} disabled={espera > 0 || pedindo || busy}>
          {espera > 0 ? t('confirmar.reenviarEm', { n: espera }) : t('confirmar.reenviar')}
        </button>
        {' · '}
        <button type="button" className={c.elo} onClick={() => { limpar(); setCodigo(''); setSenha(''); setPasso('pedir'); }} disabled={busy}>
          {t('confirmar.outroEmail')}
        </button>
        {' · '}
        <button type="button" className={c.elo} onClick={() => { limpar(); onVoltar(); }} disabled={busy}>
          {t('senha.voltarEntrar')}
        </button>
      </p>
    </form>
  );
}
